import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import {
  Category,
  CategoryTable,
  CategoryUsageDetail,
  CategoryUsageItem,
} from "@/types/category";

import { requestData } from "@/api/api-config";

/**
 * Deduplicates and merges two arrays of CategoryUsageItem by id.
 */
function mergeUsageItems(
  target: CategoryUsageItem[],
  source: CategoryUsageItem[],
): CategoryUsageItem[] {
  return Array.from(
    new Map([...target, ...source].map((item) => [item.id, item])).values(),
  );
}

/**
 * Merges usage data from a source category into a target category.
 */
function mergeUsage(
  target: CategoryUsageDetail,
  source: CategoryUsageDetail,
): CategoryUsageDetail {
  return {
    items: mergeUsageItems(target.items, source.items),
    commissions: mergeUsageItems(target.commissions, source.commissions),
    memberships: mergeUsageItems(target.memberships, source.memberships),
  };
}

/**
 * Optimistically updates the table cache by moving usage from source to target category,
 * then removing the source. Falls back to simply removing the source if either isn't found.
 */
function optimisticallyMergeCategoryUsage(
  queryClient: QueryClient,
  tableKey: readonly unknown[],
  previousTableData: CategoryTable[],
  sourceName: string,
  targetName: string,
) {
  const sourceTable = previousTableData.find((c) => c.name === sourceName);
  const targetTable = previousTableData.find((c) => c.name === targetName);

  if (sourceTable && targetTable) {
    const merged = mergeUsage(targetTable.usage, sourceTable.usage);
    queryClient.setQueryData<CategoryTable[]>(
      tableKey,
      previousTableData
        .filter((c) => c.name !== sourceName)
        .map((c) => (c.name === targetName ? { ...c, usage: merged } : c)),
    );
  } else {
    queryClient.setQueryData<CategoryTable[]>(
      tableKey,
      previousTableData.filter((c) => c.name !== sourceName),
    );
  }
}

/**
 * Cascades a category name change (or nullification) across all cached queries
 * by updating `category` and `itemCategory` fields.
 */
function cascadeCategoryReferences(
  queryClient: QueryClient,
  oldName: string,
  newName: string | null,
) {
  const entries = queryClient.getQueriesData<unknown>({
    predicate: () => true,
  });
  for (const [queryKey, data] of entries) {
    let changed = false;

    const updateOne = (value: unknown): unknown => {
      if (!value || typeof value !== "object") return value;
      const record = value as Record<string, unknown>;
      let next: Record<string, unknown> | null = null;

      const maybeSet = (field: string) => {
        if (field in record && record[field] === oldName) {
          if (!next) next = { ...record };
          next[field] = newName;
        }
      };

      maybeSet("category");
      maybeSet("itemCategory");

      return next ?? record;
    };

    if (Array.isArray(data)) {
      const nextArr = data.map(updateOne);
      changed = nextArr.some((v, i) => v !== (data as unknown[])[i]);
      if (changed) queryClient.setQueryData(queryKey, nextArr);
    } else if (data && typeof data === "object") {
      const nextObj = updateOne(data);
      changed = nextObj !== data;
      if (changed) queryClient.setQueryData(queryKey, nextObj);
    }
  }
}

export const categoryQueryKeys = {
  all: ["inventory", "category"] as const,
  list: () => [...categoryQueryKeys.all, "list"] as const,
  table: () => [...categoryQueryKeys.all, "table"] as const,
};

export const categoryQueryOptions = {
  list: () => ({
    queryKey: categoryQueryKeys.list(),
    queryFn: ({ signal }: { signal: AbortSignal }) => listCategories(signal),
  }),
  table: () => ({
    queryKey: categoryQueryKeys.table(),
    queryFn: () => listCategoriesTable(),
  }),
};

export const useCreateCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (category: Category) => createCategory(category),
    onMutate: async (newCategory: Category) => {
      await queryClient.cancelQueries({ queryKey: categoryQueryKeys.list() });
      await queryClient.cancelQueries({ queryKey: categoryQueryKeys.table() });

      const optimisticCategory: Category = {
        name: newCategory.name,
        description: newCategory.description,
      };

      const listKey = categoryQueryKeys.list();
      const previousData = queryClient.getQueryData<Category[]>(listKey) ?? [];
      queryClient.setQueryData<Category[]>(listKey, [
        ...previousData,
        optimisticCategory,
      ]);

      return { previousData, optimisticName: optimisticCategory.name };
    },
    onError: (_error, _newCategory, context) => {
      if (!context) return;
      const listKey = categoryQueryKeys.list();
      queryClient.setQueryData<Category[] | undefined>(
        listKey,
        context.previousData,
      );
    },
    onSuccess: (created, _newCategory, context) => {
      if (!context || !created) return;
      const listKey = categoryQueryKeys.list();
      const current = queryClient.getQueryData<Category[]>(listKey) ?? [];
      const replaced = current.map((c) =>
        c.name === context.optimisticName ? created : c,
      );
      queryClient.setQueryData<Category[]>(listKey, replaced);
      queryClient.invalidateQueries({ queryKey: categoryQueryKeys.table() });
    },
  });
};

export const useUpdateCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { name: string; data: Category }) =>
      updateCategory(payload.name, payload.data),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: categoryQueryKeys.list() });
      await queryClient.cancelQueries({ queryKey: categoryQueryKeys.table() });

      const { name: oldName, data } = payload;
      const newName = data.name ?? oldName;

      // Snapshot both caches for rollback
      const listKey = categoryQueryKeys.list();
      const tableKey = categoryQueryKeys.table();
      const previousListData =
        queryClient.getQueryData<Category[]>(listKey) ?? [];
      const previousTableData =
        queryClient.getQueryData<CategoryTable[]>(tableKey) ?? [];

      const exists = previousListData.some((c) => c.name === oldName);
      if (!exists) return { previousListData, previousTableData, oldName };

      // Check if we're renaming to an existing category (merge scenario)
      const targetExists =
        newName !== oldName && previousListData.some((c) => c.name === newName);

      if (targetExists) {
        // Merge: Remove source category, keep target category
        queryClient.setQueryData<Category[]>(
          listKey,
          previousListData.filter((c) => c.name !== oldName),
        );

        optimisticallyMergeCategoryUsage(
          queryClient,
          tableKey,
          previousTableData,
          oldName,
          newName,
        );

        cascadeCategoryReferences(queryClient, oldName, newName);

        return {
          previousListData,
          previousTableData,
          oldName,
          newName,
          isMerge: true,
        };
      } else {
        // Normal update: Update the category in place
        queryClient.setQueryData<Category[]>(
          listKey,
          previousListData.map((c) =>
            c.name === oldName
              ? { ...c, name: newName, description: data.description }
              : c,
          ),
        );

        // Also update TABLE cache
        queryClient.setQueryData<CategoryTable[]>(
          tableKey,
          previousTableData.map((c) =>
            c.name === oldName
              ? { ...c, name: newName, description: data.description }
              : c,
          ),
        );

        return {
          previousListData,
          previousTableData,
          oldName,
          newName,
          isMerge: false,
        };
      }
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      queryClient.setQueryData<Category[] | undefined>(
        categoryQueryKeys.list(),
        context.previousListData,
      );
      queryClient.setQueryData<CategoryTable[] | undefined>(
        categoryQueryKeys.table(),
        context.previousTableData,
      );
    },
    onSuccess: (updated, _variables, context) => {
      if (!context || !updated) return;
      const listKey = categoryQueryKeys.list();
      const current = queryClient.getQueryData<Category[]>(listKey) ?? [];

      if (context.isMerge) {
        const next = current.filter((c) => c.name !== context.oldName);
        const targetExists = next.some((c) => c.name === updated.name);
        if (!targetExists) {
          next.push(updated);
        }
        queryClient.setQueryData<Category[]>(listKey, next);
      } else {
        const next = current.map((c) =>
          c.name === context.oldName || c.name === updated.name ? updated : c,
        );
        queryClient.setQueryData<Category[]>(listKey, next);
      }

      if (context.oldName !== updated.name) {
        cascadeCategoryReferences(queryClient, context.oldName, updated.name);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: categoryQueryKeys.list() });
      queryClient.invalidateQueries({ queryKey: categoryQueryKeys.table() });
    },
  });
};

export const useDeleteCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { name: string; reassignTo?: string | null }) =>
      deleteCategory(payload.name, payload.reassignTo),
    onMutate: async (payload) => {
      const { name, reassignTo } = payload;
      await queryClient.cancelQueries({ queryKey: categoryQueryKeys.list() });
      await queryClient.cancelQueries({ queryKey: categoryQueryKeys.table() });

      const listKey = categoryQueryKeys.list();
      const tableKey = categoryQueryKeys.table();
      const previousListData =
        queryClient.getQueryData<Category[]>(listKey) ?? [];
      const previousTableData =
        queryClient.getQueryData<CategoryTable[]>(tableKey) ?? [];

      // Remove the archived category from the list cache
      queryClient.setQueryData<Category[]>(
        listKey,
        previousListData.filter((c) => c.name !== name),
      );

      // If reassigning to another category, move usage items in the table cache
      if (reassignTo) {
        optimisticallyMergeCategoryUsage(
          queryClient,
          tableKey,
          previousTableData,
          name,
          reassignTo,
        );
      } else {
        queryClient.setQueryData<CategoryTable[]>(
          tableKey,
          previousTableData.filter((c) => c.name !== name),
        );
      }

      return { previousListData, previousTableData, name, reassignTo };
    },
    onError: (_error, _payload, context) => {
      if (!context) return;
      queryClient.setQueryData<Category[] | undefined>(
        categoryQueryKeys.list(),
        context.previousListData,
      );
      queryClient.setQueryData<CategoryTable[] | undefined>(
        categoryQueryKeys.table(),
        context.previousTableData,
      );
    },
    onSuccess: (_data, _payload, context) => {
      if (!context) return;
      const targetName = context.reassignTo || null;
      cascadeCategoryReferences(queryClient, context.name, targetName);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: categoryQueryKeys.list() });
      queryClient.invalidateQueries({ queryKey: categoryQueryKeys.table() });
    },
  });
};

const listCategories = async (signal?: AbortSignal): Promise<Category[]> => {
  return (
    (await requestData<Category[]>(
      "admin/categories",
      "get",
      { signal },
      false,
    )) ?? []
  );
};

const listCategoriesTable = async (): Promise<CategoryTable[]> => {
  return (
    (await requestData<CategoryTable[]>(
      "admin/categories/table",
      "get",
      undefined,
      false,
    )) ?? []
  );
};

const createCategory = async (category: Category): Promise<Category | null> => {
  return (
    (await requestData<Category>(
      "admin/categories",
      "post",
      {
        json: category,
      },
      false,
    )) ?? null
  );
};

const updateCategory = async (
  name: string,
  category: Category,
): Promise<Category | null> => {
  return (
    (await requestData<Category>(
      `admin/categories/${encodeURIComponent(name)}`,
      "put",
      {
        json: category,
      },
      false,
    )) ?? null
  );
};

const deleteCategory = async (
  name: string,
  reassignTo?: string | null,
): Promise<null> => {
  let url = `admin/categories/${encodeURIComponent(name)}`;
  // When reassignTo is provided (string or null), pass it as a query param.
  // null → reassign_to="" (backend interprets as "no category").
  // undefined → no param (simple archive, no reassignment).
  if (reassignTo !== undefined) {
    url += `?reassign_to=${encodeURIComponent(reassignTo ?? "")}`;
  }
  return (await requestData<null>(url, "delete", undefined, false)) ?? null;
};

export const reorderCategories = async (
  orders: { name: string; displayOrder: number }[],
): Promise<Category[] | null> => {
  return await requestData<Category[]>(
    "admin/categories/reorder",
    "put",
    {
      json: {
        orders: orders.map((o) => ({
          name: o.name,
          display_order: o.displayOrder,
        })),
      },
    },
    false,
  );
};
